import { forwardRef, useState, ChangeEvent, FocusEvent } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { validateCuit } from '../utils/validation';
import { cuitDigits, formatCuit } from '../utils/format';

interface CuitInputProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'type'> {
  /** Valor controlado (puede venir con o sin guiones, se normaliza internamente) */
  value: string;
  /** Recibe el CUIT formateado (XX-XXXXXXXX-X). Para guardar usar cuitDigits() */
  onChange: (value: string) => void;
  /** Texto de la etiqueta. Default: "CUIT" */
  label?: string;
  /** Si validar dígito verificador AFIP en blur. Default: true */
  validateOnBlur?: boolean;
  /** Mensaje custom cuando el CUIT es inválido */
  invalidMessage?: string;
}

/**
 * Input controlado para CUIT/CUIL con:
 *  - Maximo 11 digitos
 *  - Formato visual XX-XXXXXXXX-X mientras el usuario tipea
 *  - Validacion de digito verificador AFIP al blur
 *  - Mensajes de error inline
 *
 * Funciona tanto para CUIT de empresa como CUIL de afiliado (mismo algoritmo).
 */
const CuitInput = forwardRef<HTMLInputElement, CuitInputProps>(function CuitInput(
  {
    value,
    onChange,
    label = 'CUIT',
    validateOnBlur = true,
    invalidMessage,
    error,
    helperText,
    onBlur,
    ...rest
  },
  ref
) {
  const [touched, setTouched] = useState(false);

  // Mostrar el valor formateado
  const display = formatCuit(value) === '-' ? value : formatCuit(value);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Tomar solo digitos, limitar a 11
    const digits = cuitDigits(e.target.value).slice(0, 11);
    // Devolver el valor formateado para que el formulario vea siempre XX-XXXXXXXX-X
    if (digits.length === 11) {
      onChange(formatCuit(digits));
    } else {
      onChange(digits);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    onBlur?.(e);
  };

  // Estado de error
  const digits = cuitDigits(value);
  const isComplete = digits.length === 11;
  const isInvalid = touched && validateOnBlur && isComplete && !validateCuit(digits);
  const isIncomplete = touched && digits.length > 0 && digits.length < 11;

  const hasError = error || isInvalid || isIncomplete;
  let messageText = helperText;
  if (isInvalid) messageText = invalidMessage ?? 'CUIT/CUIL invalido (digito verificador incorrecto)';
  else if (isIncomplete) messageText = 'Debe tener 11 digitos';

  return (
    <TextField
      {...rest}
      inputRef={ref}
      type="text"
      label={label}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!hasError}
      helperText={messageText}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 13, // 11 digitos + 2 guiones
        placeholder: 'XX-XXXXXXXX-X',
        ...rest.inputProps,
      }}
    />
  );
});

export default CuitInput;
